import { doc, getDoc, setDoc, runTransaction } from 'firebase/firestore';
import { db } from './firebase';


const VISITOR_STATS_DOC = 'siteStats';
const VISITOR_COUNT_ID = 'visitors';
const LOCAL_STORAGE_KEY = 'hasVisitedSite';

/**
 * Initialize visitor stats document if it doesn't exist
 */
const initializeVisitorStats = async () => {
    try {
        const docRef = doc(db, VISITOR_STATS_DOC, VISITOR_COUNT_ID);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            await setDoc(docRef, { count: 0 });
            console.log('Visitor stats initialized');
        }
    } catch (error) {
        console.error('Error initializing visitor stats:', error);
    }
};

/**
 * Get current visitor count from Firebase
 * @returns {Promise<number>} Current visitor count
 */
export const getVisitorCount = async () => {
    try {
        const docRef = doc(db, VISITOR_STATS_DOC, VISITOR_COUNT_ID);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data().count || 0;
        } else {
            // Initialize if doesn't exist
            await initializeVisitorStats();
            return 0;
        }
    } catch (error) {
        console.error('Error getting visitor count:', error);
        return 0;
    }
};

/**
 * Increment visitor count in Firebase
 * @returns {Promise<number>} New visitor count
 */
const incrementVisitorCount = async () => {
    // tackles race conditions
    const docRef = doc(db, VISITOR_STATS_DOC, VISITOR_COUNT_ID);
    return await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        const currentCount = docSnap.data().count || 0;

        const newCount = currentCount + 1;

        transaction.update(docRef, { count: newCount });

        return newCount;
    });
};

/**
 * Check if user has visited before using localStorage
 * @returns {boolean} True if user has visited before
 */
const hasVisitedBefore = () => {
    try {
        return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
    } catch (error) {
        console.error('Error checking localStorage:', error);
        return false;
    }
};

/**
 * Mark user as having visited in localStorage
 */
const markAsVisited = () => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    } catch (error) {
        console.error('Error setting localStorage:', error);
    }
};

/**
 * Main function to track visitor and get count
 * Call this once when homepage loads
 * @returns {Promise<number>} Current visitor count
 */
export const trackVisitor = async () => {
    try {
        if (hasVisitedBefore()) {
            return await getVisitorCount();
        } else {
            const newCount = await incrementVisitorCount();
            markAsVisited();
            return newCount;
        }
    } catch (error) {
        console.error('Error tracking visitor:', error);
        return await getVisitorCount();
    }
};

/**
 * Reset visitor flag in localStorage (for testing purposes)
 * NOT TO BE USED IN PRODUCTION - just for development/testing
 */
export const resetVisitorFlag = () => {
    try {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        console.log('Visitor flag reset - next visit will count as new');
    } catch (error) {
        console.error('Error resetting visitor flag:', error);
    }
};